import { Suspense } from 'react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/react';
import ChatAvatarCanvas from '~/components/ChatAvatar/Canvas/Canvas';
import ChatPageLoadingLayout from '~/components/common/ChatPageLoadingLayout/ChatPageLoadingLayout';
import { AvatarInfo } from '~/types';
import NoAvatarIcon from '~/assets/img/completion_images/not_yet.svg';
import useChatPageLoadingStatus from '~/store/useChatPageLoadingStatusStore';

interface ThreeJSBackgroundProps {
  avatarInfo: AvatarInfo[] | null;
  /**
   * 3D 배경 이미지 주소
   */
  bgTextureUrl?: string;
  /**
   * 2D 배경 이미지 주소
   */
  bgImageUrl?: string;
  isAvatarVisible?: boolean;
  isOverviewMode?: boolean;
}

export const AvatarLoadFailFallbackLayout = () => {
  const { loadingBg } = useChatPageLoadingStatus();

  return (
    <FullScreenContainer
      style={{
        background: loadingBg ? `url(${loadingBg})` : '#000000',
        backdropFilter: 'blur(20px)',
      }}
    >
      <FullScreenBlurContainer>
        <AvatarErrorText>Failed to Load 3D Avatar</AvatarErrorText>
        <img title='avatar loading failed' src={NoAvatarIcon} />
      </FullScreenBlurContainer>
    </FullScreenContainer>
  );
};

export default function ThreeJSBackground({
  avatarInfo,
  bgTextureUrl,
  bgImageUrl,
  isAvatarVisible = true,
  isOverviewMode = false,
}: ThreeJSBackgroundProps) {
  return (
    <div>
      <ChatPageLoadingLayout />
      <Container>
        <Sentry.ErrorBoundary fallback={<AvatarLoadFailFallbackLayout />}>
          <Suspense fallback={<></>}>
            <ChatAvatarCanvas
              avatars={avatarInfo}
              group={!!avatarInfo && avatarInfo.length > 1}
              bgTextureUrl={bgTextureUrl}
              bgImageUrl={bgImageUrl}
              visible={isAvatarVisible}
              isOverviewMode={isOverviewMode}
            />
          </Suspense>
        </Sentry.ErrorBoundary>
      </Container>
    </div>
  );
}
export const Container = styled.div`
  position: absolute;
  z-index: 0;
  width: 100%;
  height: 100%;
`;

export const FullScreenContainer = styled.div`
  position: absolute;
  z-index: 20;
  width: 100%;
  height: 100vh;
  backdrop-filter: blur(20px);
  background-color: black;
  background-position: center;
  background-size: cover;
  inset: 0;
  object-fit: cover;
`;

const FullScreenBlurContainer = styled.div`
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(20px);
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
  gap: 24px;
`;

const AvatarErrorText = styled.span`
  ${({ theme }) => theme.fontStyle.text.body18_bold}
  color: ${({ theme }) => theme.content.content_light_1}
`;
